import { OTPInput, OTPInputContext } from 'input-otp';
import { ComponentProps, useContext } from 'react';

import { MinusIcon } from '@/assets/icons';
import { mergeClasses } from '@/utils';

import { IInputOTP } from './InputOTP.interfaces';

const InputOTP = ({
  length,
  value,
  onChange,
  label,
  required,
  pattern,
  disabled,
  className,
  containerClassName,
  children,
}: IInputOTP) => {
  return (
    <div className={mergeClasses('flex flex-col gap-1.5 border-neutral-n4 w-full', className)}>
      {label && (
        <label className="text-sm font-semibold text-neutral-n9 text-center">
          {label}
          {required && <span className="ml-1 text-semantic-error-base">*</span>}
        </label>
      )}
      <OTPInput
        data-slot="input-otp"
        maxLength={length}
        value={value}
        onChange={onChange}
        pattern={pattern}
        disabled={disabled}
        containerClassName={mergeClasses(
          'cn-input-otp flex items-center has-disabled:opacity-50',
          containerClassName,
        )}
        spellCheck={false}
        className={mergeClasses('disabled:cursor-not-allowed', className)}
      >
        {children}
      </OTPInput>
    </div>
  );
};

const InputOTPGroup = ({ className, ...props }: ComponentProps<'div'>) => {
  return (
    <div
      data-slot="input-otp-group"
      className={mergeClasses(
        'has-aria-invalid:ring-destructive/20 dark:has-aria-invalid:ring-destructive/40 has-aria-invalid:border-destructive rounded-lg has-aria-invalid:ring-[3px] flex items-center gap-2',
        className,
      )}
      {...props}
    />
  );
};

const InputOTPSlot = ({
  index,
  className,
  isError,
  ...props
}: ComponentProps<'div'> & {
  index: number;
  isError?: boolean;
}) => {
  const inputOTPContext = useContext(OTPInputContext);
  const { char, isActive } = inputOTPContext?.slots[index] ?? {};

  return (
    <div
      data-slot="input-otp-slot"
      data-active={isActive}
      className={mergeClasses(
        'dark:bg-input/30 border-input data-[active=true]:border-brand-primary-base aria-invalid:border-destructive data-[active=true]:aria-invalid:border-destructive size-8 border-y border-r transition-all outline-none first:rounded-l-lg first:border-l last:rounded-r-lg relative flex items-center justify-center data-[active=true]:z-10 w-20 h-20 bg-white rounded-soft border border-solid border-neutral-n4 text-4xl font-bold text-brand-primary-darkest',
        isError && 'border-semantic-error-base',
        className,
      )}
      {...props}
    >
      {char}
    </div>
  );
};

const InputOTPSeparator = ({ ...props }: ComponentProps<'div'>) => {
  return (
    <div
      data-slot="input-otp-separator"
      className="[&_svg:not([class*='size-'])]:size-4 flex items-center"
      role="separator"
      {...props}
    >
      <MinusIcon />
    </div>
  );
};

export { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot };
